import { EMPTY_FILTERS, type AreaId, type Filters, type SourceId } from '../types';

/**
 * Filtervalen ligger kvar mellan besöken. En sökning man skrivit ska inte
 * försvinna för att man laddar om eller går till en annons och tillbaka.
 *
 * Nyckeln är versionsmärkt: ändrar Filters form kan den gamla lagringen
 * ignoreras genom att räkna upp versionen i stället för att läsa skräp.
 */
const KEY = 'uppdragsradar.filters.v1';

export function readStoredFilters(): Filters {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return EMPTY_FILTERS;
    return sanitize(JSON.parse(raw) as unknown);
  } catch {
    // Privat läge, blockerade kakor eller trasigt innehåll. Ingen av delarna
    // är värd att störa användaren med – filtren börjar bara från noll.
    return EMPTY_FILTERS;
  }
}

export function writeStoredFilters(filters: Filters): void {
  try {
    if (isEmpty(filters)) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, JSON.stringify(filters));
  } catch {
    // Att inte kunna spara filtret får aldrig hindra själva filtreringen.
  }
}

export function isEmpty(filters: Filters): boolean {
  return (
    filters.tech.length === 0 &&
    filters.year === undefined &&
    filters.month === undefined &&
    filters.area === undefined &&
    filters.source === undefined &&
    filters.location === undefined &&
    filters.seniority === undefined &&
    filters.workMode === undefined &&
    !filters.search &&
    filters.datedOnly !== true &&
    filters.hideRemoved !== true &&
    filters.openOnly !== true &&
    filters.showDuplicates !== true
  );
}

/**
 * Det som ligger i lagringen kan vara skrivet av en äldre version av appen,
 * eller handredigerat. Bara fält med rätt typ tas med; resten faller bort.
 */
function sanitize(value: unknown): Filters {
  if (!value || typeof value !== 'object') return EMPTY_FILTERS;
  const raw = value as Record<string, unknown>;

  return {
    tech: Array.isArray(raw.tech) ? raw.tech.filter((tech): tech is string => typeof tech === 'string') : [],
    ...(typeof raw.year === 'number' && Number.isFinite(raw.year) ? { year: raw.year } : {}),
    ...(typeof raw.month === 'number' && raw.month >= 1 && raw.month <= 12 ? { month: raw.month } : {}),
    ...(typeof raw.area === 'string' ? { area: raw.area as AreaId } : {}),
    ...(typeof raw.source === 'string' ? { source: raw.source as SourceId } : {}),
    ...(typeof raw.location === 'string' ? { location: raw.location } : {}),
    ...(typeof raw.seniority === 'string' ? { seniority: raw.seniority } : {}),
    ...(typeof raw.workMode === 'string' ? { workMode: raw.workMode } : {}),
    ...(typeof raw.search === 'string' && raw.search.trim() ? { search: raw.search } : {}),
    ...(raw.datedOnly === true ? { datedOnly: true } : {}),
    ...(raw.hideRemoved === true ? { hideRemoved: true } : {}),
    ...(raw.openOnly === true ? { openOnly: true } : {}),
    ...(raw.showDuplicates === true ? { showDuplicates: true } : {}),
  };
}
